import React, {useContext, useEffect, useState, useRef} from 'react';
import { Container, Row, Col, Image, Form, Button, Badge, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate} from "react-router-dom";
import { AppContext } from '../../AppContext';
import './chatmessage.scss';
import {format} from 'timeago.js';
import { incrementUnreadCount } from '../../redux/messagesSlice';
import axios from 'axios';



const normalizeVideoUrls = (urls) => {
  if (!Array.isArray(urls)) return [];

  return urls
    .filter((url) => typeof url === "string")
    .map((url) => url.trim())
    .filter(Boolean);
};

const normalizeTrainingVideos = (videos = []) => {
  if (!Array.isArray(videos)) return [];

  return videos
    .map((video, index) => {
      // Backward compatible with old data:
      // ["https://youtube.com/..."]
      if (typeof video === "string") {
        const cleanUrl = video.trim();

        return {
          title: index === 0 ? "Quick Start Video" : `Training Video ${index + 1}`,
          url: cleanUrl,
        };
      }

      // New data:
      // [{ title: "...", url: "..." }]
      if (video && typeof video === "object") {
        const cleanUrl = typeof video.url === "string" ? video.url.trim() : "";
        const cleanTitle =
          typeof video.title === "string" && video.title.trim()
            ? video.title.trim()
            : index === 0
            ? "Quick Start Video"
            : `Training Video ${index + 1}`;

        return {
          title: cleanTitle,
          url: cleanUrl,
        };
      }

      return null;
    })
    .filter((video) => video?.url);
};


const getVideoEmbedUrl = (url, { autoplay = false } = {}) => {
  if (!url || typeof url !== "string") return null;

  try {
    const parsed = new URL(url);
    let embedUrl = null;

    // YouTube normal:
    // https://www.youtube.com/watch?v=VIDEO_ID
    if (parsed.hostname.includes("youtube.com") && parsed.searchParams.get("v")) {
      embedUrl = `https://www.youtube.com/embed/${parsed.searchParams.get("v")}`;
    }

    // YouTube short:
    // https://youtu.be/VIDEO_ID
    if (parsed.hostname.includes("youtu.be")) {
      const videoId = parsed.pathname.replace("/", "");
      embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }

    // YouTube shorts:
    // https://www.youtube.com/shorts/VIDEO_ID
    if (
      parsed.hostname.includes("youtube.com") &&
      parsed.pathname.startsWith("/shorts/")
    ) {
      const videoId = parsed.pathname.split("/shorts/")[1];
      embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }

    // Already embedded YouTube URL
    if (
      parsed.hostname.includes("youtube.com") &&
      parsed.pathname.startsWith("/embed/")
    ) {
      embedUrl = url;
    }

    // Vimeo
    if (parsed.hostname.includes("vimeo.com")) {
      if (parsed.hostname.includes("player.vimeo.com")) {
        embedUrl = url;
      } else {
        const videoId = parsed.pathname.split("/").filter(Boolean)[0];
        embedUrl = videoId ? `https://player.vimeo.com/video/${videoId}` : null;
      }
    }

    // Wistia normal share link:
    // https://youraccount.wistia.com/medias/VIDEO_ID
    if (parsed.hostname.includes("wistia.com")) {
      if (
        parsed.hostname.includes("fast.wistia.net") &&
        parsed.pathname.startsWith("/embed/iframe/")
      ) {
        embedUrl = url;
      } else if (parsed.pathname.includes("/medias/")) {
        const videoId = parsed.pathname.split("/medias/")[1]?.split("/")[0];
        embedUrl = videoId
          ? `https://fast.wistia.net/embed/iframe/${videoId}`
          : null;
      }
    }


    if (!embedUrl) return null;

    const embedParsed = new URL(embedUrl);

    if (embedParsed.hostname.includes("youtube.com")) {
      embedParsed.searchParams.set("controls", "1");
      embedParsed.searchParams.set("playsinline", "1");
      embedParsed.searchParams.set("rel", "0");
      embedParsed.searchParams.set("modestbranding", "1");
    
    }

    if (embedParsed.hostname.includes("vimeo.com")) {
      embedParsed.searchParams.set("playsinline", "1");

    }

    if (embedParsed.hostname.includes("wistia.net")) {
      embedParsed.searchParams.set("videoFoam", "true");
      embedParsed.searchParams.set("playsinline", "true");
      embedParsed.searchParams.delete("autoplay");
      embedParsed.searchParams.delete("muted");
    }



    return embedParsed.toString();
  } catch (e) {
    return null;
  }
};





const getVideoHost = (url) => {
  if (!url || typeof url !== "string") return "Video";

  if (url.includes("youtube.com") || url.includes("youtu.be")) return "YouTube";
  if (url.includes("vimeo.com")) return "Vimeo";
  if (url.includes("wistia.com") || url.includes("wistia.net")) return "Wistia";

  return "Video";
};

const TrainingVideoCard = ({
  videos,
  onOpen,
  docsUrl,
  faqUrl,
  replacementPolicyUrl,
}) => {
  if (!videos?.length) return null;

  const firstVideo = videos[0];

  return (
    <div className="training-video-wrap">
      <div className="training-video-card" onClick={() => onOpen(0)}>
        <div className="training-video-thumb">
          <div className="training-video-brand">IncentiFire</div>
          <div className="training-video-play">▶</div>
          <div className="training-video-time">Video</div>
        </div>

        <div className="training-video-copy">
          <h4>{firstVideo.title || "Quick Start Video"}</h4>
          <p>Watch the training videos for this offer.</p>
          <button type="button" className="training-video-link">
            Watch training ›
          </button>
        </div>
      </div>

      <div className="training-help-row">
        {docsUrl && (
          <a
            href={docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="training-help-pill"
          >
            Docs
            <span>Guides & help</span>
          </a>
        )}

        {faqUrl && (
          <a
            href={faqUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="training-help-pill"
          >
            FAQ
            <span>Answers</span>
          </a>
        )}

        {replacementPolicyUrl && (
          <a
            href={replacementPolicyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="training-help-pill"
          >
            Replacement
            <span>Policy</span>
          </a>
        )}
      </div>
    </div>
  );
};



const TrainingVideoModal = ({
  show,
  onHide,
  videos,
  selectedVideoIndex,
  setSelectedVideoIndex,
}) => {
  if (!videos?.length) return null;

  const currentVideo = videos[selectedVideoIndex];
  const currentUrl = currentVideo?.url;
  const currentTitle =
    currentVideo?.title || `Training Video ${selectedVideoIndex + 1}`;

  const embedUrl = getVideoEmbedUrl(currentUrl);

  const handlePrev = () => {
    setSelectedVideoIndex((prev) =>
      prev === 0 ? videos.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setSelectedVideoIndex((prev) =>
      prev === videos.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="training-modal-shell"
      dialogClassName="training-modal-dialog"
      contentClassName="training-modal-content"
    >
      <Modal.Header closeButton>
        <Modal.Title>{currentTitle}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="training-player-wrap">
          {embedUrl ? (
            <iframe
              key={`${selectedVideoIndex}-${currentUrl}`}
              className="training-player"
              src={embedUrl}
              title={currentTitle}
              allow="fullscreen; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <div className="training-player-fallback">
              <p>This video cannot be embedded.</p>
              <Button
                variant="primary"
                onClick={() =>
                  window.open(currentUrl, "_blank", "noopener,noreferrer")
                }
              >
                Open video
              </Button>
            </div>
          )}
        </div>
{/* 
        <Button
          variant="link"
          className="open-native-video-link"
          onClick={() =>
            window.open(currentUrl, "_blank", "noopener,noreferrer")
          }
        >
          Open video in new tab
        </Button> */}

        {videos.length > 1 && (
          <div className="training-modal-actions">
            <Button variant="light" onClick={handlePrev}>
              ‹
            </Button>

            <span>
              Lesson {selectedVideoIndex + 1} of {videos.length}
            </span>

            <Button variant="light" onClick={handleNext}>
              ›
            </Button>
          </div>
        )}

        <div className="training-lesson-list">
          {videos.map((video, index) => (
            <button
              key={`${video.url}-${index}`}
              type="button"
              className={
                index === selectedVideoIndex
                  ? "training-lesson active"
                  : "training-lesson"
              }
              onClick={() => setSelectedVideoIndex(index)}
            >
              <span className="training-lesson-number">{index + 1}</span>

              <span className="training-lesson-copy">
                <strong>
                  {video.title || `Training Video ${index + 1}`}
                </strong>
                <small>{getVideoHost(video.url)} training video</small>
              </span>

              <span className="training-lesson-arrow">›</span>
            </button>
          ))}
        </div>
      </Modal.Body>
    </Modal>
  );
};





const ChatMessage = () => {

  const location = useLocation();
  const navigate = useNavigate();
  const { profiles, eventTitle, eventDate, eventLead } = location.state;  
  const socket = useContext(AppContext); 
  const [message, setMessage] = useState(''); 
  const [messages, setMessages] = useState([]); 
  const {currentUser} = useSelector(state => state.user); 
  const formRef = useRef(null); 
  const isMounted = useRef(true);
  const messageEndRef = useRef(null);
  const dispatch = useDispatch(); 

  //Add vidoe training components 
  //profiles.video_urls = ["https://www.youtube.com/watch?v=tfgfSubAEJM", "https://www.youtube.com/watch?v=KO7MxL6bKj4"]



  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);


  const trainingVideos = normalizeTrainingVideos(
    profiles?.video_urls || profiles?.videoUrls || []
  );
  
  const hasTrainingVideos = trainingVideos.length > 0;



  const DEMO_LEAD_DELIVERY = {
    packageName: "CA Tech Leads (100)",
    leadType: "Software Engineers + PMs",
    state: "CA",
    leadsIncluded: 100,
    purchasedAtLabel: "Mar 6",
    lastUpdatedLabel: "Mar 6",
    googleSheetUrl: "https://docs.google.com/spreadsheets/d/EXAMPLE_SHEET_ID/edit#gid=0",
    tags: ["Paid", "Delivered"],
  };
  // ---------- UI Component: pinned "Your leads are ready" card ----------
const LeadReadyCard = ({ delivery }) => {
  const handleOpen = () => window.open(delivery.forwardUrl, "_blank", "noopener,noreferrer");

  return (
    <div className="lead-ready-wrap">
      <div className="lead-ready-card">
        <h3 className="lead-ready-title">{delivery.isLead ? 'Your leads are ready': 'Your Event Link is Below'}</h3>

        <div className="lead-ready-btn-row">
          <Button className="lead-ready-btn" variant="primary" onClick={handleOpen}>
            Open Google Sheet
          </Button>
        </div>

        <Button
          variant="link"
          className="lead-ready-link"
          onClick={() => alert("Request received. Please contact support using the chat widget and we will assist you.")}
        >
          Request a replacement lead
        </Button>

        <div className="lead-ready-meta">
          <div className="lead-ready-meta-row">
            <span className="lead-ready-meta-label">Package:</span>
            <span className="lead-ready-meta-value">{delivery.eventTitle}</span>
          </div>
          <div className="lead-ready-meta-row">
            <span className="lead-ready-meta-label">Purchased:</span>
            <span className="lead-ready-meta-value">{delivery.eventDate}</span>
          </div>
          <div className="lead-ready-meta-row">
            <span className="lead-ready-meta-label">Last updated:</span>
            <span className="lead-ready-meta-value">{delivery.forwardUrl}</span>
          </div>
        </div>

        <div className="lead-ready-tags">
          <span className="lead-ready-tag system">System</span>
          {delivery.tags.map((t) => (
            <span key={t} className={`lead-ready-tag ${t.toLowerCase()}`}>
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
 


  useEffect(() => {

    console.log('Use EFEFECT RUNS'); 
    if (socket) {
      socket.on('disconnect', () => {
        console.log('Socket disconnected');
        // Redirect to home page or login page
        //navigate('/login');
      });


      socket.on('prev-messages', (messages)=>{
        setMessages(messages);
      });

      socket.on("room-messages", (roomMessage) => {
        setMessages(prevMessages => [...prevMessages, roomMessage]);
      });

      // emit should be after the 'prev' message listener is setup check if resolves issue
      socket.emit("join-room", profiles._id);
      console.log('JOIN ROOM EFEFECT RUNS'); 

      // Move increment logic to navbar so it's centralized
      // socket.on("increment-message", async (room)=>{
      //   console.log(typeof(room));
      //   console.log(typeof(profiles._id));
      //   console.log(room)
      //   console.log(profiles._id);
      //   console.log(profiles._id !== room);
      //   if(profiles._id !== room) {
      //     dispatch(incrementUnreadCount(room));

      //     try{

      //       const response = await axios.post('/api/messages/incrementUnreadCount',{roomId: profiles._id});

      //     }catch(e){
      //       console.log('Failed to set unread message ', e);
      //     }
      //   }  
      // });



      socket.on('error', (error) => {
        console.error('Socket encountered an error:', error);
        // Redirect to home page or login page
        navigate('/login');
      });
    }

    // Cleanup listeners when component unmounts
    return () => {
      if (socket) {
        // takes care of having to leave rooms
        socket.emit('leaveRoom', profiles._id);
        // possibly add dispatch event to set messageStore's current room to '' to only notify when not in the current room
        socket.off('error');
        socket.off('room-messages'); 
        socket.off('prev-messages');
        //socket.off('increment-message');
      }
    };
  }, [socket]);

  useEffect (()=> {
    //console.log(messages);
    //console.log(profiles._id); 
    // socket.off("room-messages").on("room-messages", (roomMessage) => {
    //   setMessages(prevMessages => [...prevMessages, roomMessage]);
    // });
    scrollToBottom();
  },[messages]); 


  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
        if (e.key === 'Enter' && isMounted.current) {
            handleSubmit();
        }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    // Cleanup the event listener when the component is unmounted
    return () => {
        document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, []);


  const handleSubmit = (e) =>{
    if (formRef.current && isMounted.current) {
      const today = new Date();
      const minutes = today.getMinutes() < 10 ? "0" + today.getMinutes() : today.getMinutes();
      const time = today.getHours() + ":" + minutes;
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const formattedDate = `${monthNames[today.getMonth()]} ${today.getDate()}, ${today.getFullYear().toString().substring(0,4)}`;
      const formData = new FormData(formRef.current);
      const messageValue = formData.get('message');  

      // ** TO DO ** Only Have sender._id, sender.profilePic and sender.userName remove all other fields as rest look for all currentUser and modify to only necessary info
      const {_id, userName, profilePic } = currentUser; 
      const sendUser = {_id, userName, profilePic};
      if (!messageValue) return;
      //old: socket.emit("message-room", profiles._id, messageValue, currentUser, time, formattedDate, today);
      socket.emit("message-room", profiles._id, messageValue, sendUser, time, formattedDate, today);
      setMessage('');
    }
  }

  const handleFormSubmit = (e) =>{
    e.preventDefault(); 
  }
  

  const handleImageClick = (id) =>{
    navigate(`/profile/${id}`); 
  }
  // ensuring Mounting otherwise prevent memory leaks 
  // this is a cleanup
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const scrollToBottom = () => 
  {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }




  return (   <Container fluid className="message-container py-4">
        {/* Pinned "Your leads are ready" card */}
        {eventLead && <LeadReadyCard delivery={profiles} />}
        {/* Adding Training Card */}
        {hasTrainingVideos && (
              <TrainingVideoCard
                videos={trainingVideos}
                docsUrl={profiles?.docs_url}
                faqUrl={profiles?.faq_url}
                replacementPolicyUrl={profiles?.replacement_policy_url}
                onOpen={(index) => {
                  setSelectedVideoIndex(index);
                  setShowTrainingModal(true);
                }}
              />
            )}

  <Row className="mb-3 d-flex flex-sm-row">
    <Col xs={1}>
      <Button variant="light" onClick={() => navigate("/chat")}>
        Back
      </Button>
    </Col>
    <Col xs={9}>
      <h2 className="event-title text-center">{eventTitle}</h2>
      <h6 className="event-date text-center">{eventDate?.split('T')[0]}</h6>
    </Col>
    <Col xs={1}> </Col>
  </Row>


  <Row className="profile-row mb-3">
    {profiles.guests.slice(0, 5).map((profile, index) => (
      <Col xs={2} sm={1} key={index} className="profile-col px-1">
        <div className="profile-wrap" onClick={() => handleImageClick(profile.user._id)}>
        <Image src={profile.user.profilePic} alt="Profile" className="profile-image w-100" />
        <p className='profile-name'>{profile.user.userName}</p>
        </div>
      </Col>
    ))}
    {profiles.guests.length > 5 && (
      <Col xs={2} sm={1} className="profile-col px-1 d-flex justify-content-center align-items-center overflow-profiles">
        <Badge pill variant="danger">+{profiles.guests.length - 5}</Badge>
      </Col>
    )}
  </Row>


<div className='chat-container'>
        <div className="chat-box mb-4">
        {messages?.map(({ sender, content, date, sortDate }, msgIdx) => (
                                      <div className={sender?._id == currentUser._id ? "message-bubble sender" : "message-bubble receiver"} key={msgIdx}>
                                          <div className="message-inner">
                                              <div className="d-flex align-items-left mb-3">
                                                  <img src={sender.profilePic} style={{ width: 35, height: 35, objectFit: "cover", borderRadius: "50%", marginRight: 10 }} />
                                                  <p className="message-sender" style={{ fontSize: '8', margin: 0, marginRight: 10  }}>{sender._id == currentUser?._id ? "You" : sender.userName}</p>
                                                  <p  className="ml-2" style={{ fontSize: '8'}}>{date}</p>
                                              </div>
                                              <p className="message-content">{content}</p>
                                              <p className="time-ago">{format(sortDate)}</p>
                                          </div>
        </div>))}
              <div ref={messageEndRef} />
        </div>

        <Row className="d-flex flex-column flex-sm-row justify-content-between align-items-stretch mb-3">
              <Col xs={12} sm={9} className="pe-2">
                <Form ref={formRef} onSubmit={handleFormSubmit}>
                <Form.Group className="mb-2 mb-sm-0 flex-grow-1">
                  <Form.Control name="message" type="text" placeholder="Type a message" value={message} onChange={(e) => setMessage(e.target.value)} />
                </Form.Group>
                </Form>
              </Col>
              <Col xs={12} sm={3} className="ps-2 mt-2 mt-sm-0">
                <Button variant="primary" type="button" onClick={handleSubmit} className="w-100">Send</Button>
              </Col>
              <Col xs={12} md={4} className='mt-3'>
              
              <Button className="w-100" variant="danger" onClick={() => alert("Contact Us via the support icon in the bottom right. We will reach out within 48 hours!")}>
                        Business/Customer Non-Responsive? 
              </Button>
              </Col>
        </Row>
  </div>
  {hasTrainingVideos && (
          <TrainingVideoModal
            show={showTrainingModal}
            onHide={() => setShowTrainingModal(false)}
            videos={trainingVideos}
            selectedVideoIndex={selectedVideoIndex}
            setSelectedVideoIndex={setSelectedVideoIndex}
          />
        )}
</Container>
);
}

export default ChatMessage